import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { contractApi, contractConfigApi, projectApi, type PingDeposit } from '../services/api';
import { classicSlipAmount, classicSlipCodeLine } from './PingClassicSlip';
import { PingPaymentDocument, type PingDocumentContext } from './PingPaymentDocument';
import { installPrintPage } from '../services/printPage';

export function PingDepositNotice({ initial, canSimulate, onBack, onFunded }: { initial: PingDeposit; canSimulate: boolean; onBack: () => void; onFunded?: () => void }) {
  const { t, i18n } = useTranslation();
  const [deposit, setDeposit] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [amount, setAmount] = useState('');
  const canShowClassic = classicSlipCodeLine(deposit) !== null;
  useEffect(() => {
    if (canShowClassic) return installPrintPage('ping-bankgiro');
  }, [canShowClassic]);
  const contextKey = `${deposit.paymentId}:${deposit.projectId}:${deposit.contractId}`;
  const [documentState, setDocumentState] = useState<{ key: string; context?: PingDocumentContext; error?: boolean } | null>(null);
  const documentContext = documentState?.key === contextKey ? documentState.context : undefined;
  const documentError = documentState?.key === contextKey && documentState.error;
  useEffect(() => {
    if (!canShowClassic) return;
    let active = true;
    const load = async () => {
      try {
        // Resolve from the stored payment, never from editable checkout URL parameters.
        if (!deposit.projectId || !deposit.contractId) throw new Error('Missing payment context');
        const [project, contract] = await Promise.all([
          projectApi.getProject(deposit.projectId), contractApi.getContract(deposit.contractId),
        ]);
        if (project.id !== deposit.projectId || contract.id !== deposit.contractId || contract.projectId !== project.id ||
            !project.customerName?.trim() || !project.companyName?.trim()) throw new Error('Incomplete payment context');
        if (active) setDocumentState({ key: contextKey, context: { project, contract } });
      } catch { if (active) setDocumentState({ key: contextKey, error: true }); }
    };
    void load();
    return () => { active = false; };
  }, [canShowClassic, contextKey, deposit.projectId, deposit.contractId]);
  const [fontState, setFontState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  useEffect(() => {
    if (!canShowClassic) { setFontState('idle'); return; }
    let active = true;
    setFontState('loading');
    const loaded = document.fonts?.load('12px "Etappio OCR B"', '0123456789#>H');
    if (!loaded) { setFontState('error'); return; }
    void loaded.then(fonts => { if (active) setFontState(fonts.length ? 'ready' : 'error'); }, () => { if (active) setFontState('error'); });
    return () => { active = false; };
  }, [canShowClassic]);
  const inFlight = useRef(false);
  const refreshCallback = useRef<() => void>(() => {});
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const simulation = useRef<{ reference: string; amountOre: number } | null>(null);
  const money = (ore: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: deposit.currency }).format(ore / 100);
  const refresh = async (simulate = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(false);
    try {
      const amountOre = Math.round(Number(amount.replace(',', '.')) * 100);
      if (simulate && (!Number.isSafeInteger(amountOre) || amountOre <= 0 || amountOre > 2147483647)) throw new Error('Invalid amount');
      if (simulate && !simulation.current) simulation.current = { reference: crypto.randomUUID(), amountOre };
      const result = simulate
        ? await contractConfigApi.simulatePingDeposit(deposit.paymentId, simulation.current!)
        : await contractConfigApi.syncPingDeposit(deposit.paymentId);
      if (!active.current) return;
      setDeposit(result.pingDeposit);
      if (result.pingDeposit.status === 'funded') onFunded?.();
      if (simulate) { simulation.current = null; setAmount(''); }
    } catch { setError(true); }
    finally { inFlight.current = false; setBusy(false); }
  };
  refreshCallback.current = () => { void refresh(); };
  useEffect(() => {
    if (!onFunded || deposit.status === 'funded') return;
    const check = () => { if (document.visibilityState === 'visible') refreshCallback.current(); };
    const interval = window.setInterval(check, 10000);
    window.addEventListener('focus', check);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', check); };
  }, [deposit.status, onFunded]);
  return <section className="p-3 p-sm-4 text-break" aria-labelledby="ping-deposit-title">
    <h1 id="ping-deposit-title" className="h4">{t('pingDeposit.title')}</h1>
    <p>{t('pingDeposit.instructions')}</p>
    <div role="status" className={`alert ${deposit.status === 'funded' ? 'alert-success' : 'alert-info'}`}>
      {t(`pingDeposit.status.${deposit.status}`)}
    </div>
    {deposit.errorCode?.startsWith('ping_deposit_documents_') && <p role="alert" className="alert alert-warning">{t('pingDeposit.documentsError')}</p>}
    <dl className="row g-2">
      <dt className="col-12 col-sm-6">{t('pingDeposit.amount')}</dt><dd className="col-12 col-sm-6">{money(deposit.amountOre)}</dd>
      <dt className="col-12 col-sm-6">{t('pingDeposit.received')}</dt><dd className="col-12 col-sm-6">{deposit.receivedOre === null ? t('pingDeposit.notConfirmed') : money(deposit.receivedOre)}</dd>
      <dt className="col-12 col-sm-6">{t('pingDeposit.confirmed')}</dt><dd className="col-12 col-sm-6">{money(deposit.confirmedOre)}</dd>
      {deposit.notice && <>
        <dt className="col-12 col-sm-6">{t('pingDeposit.reference')}</dt><dd className="col-12 col-sm-6">{deposit.notice.reference}</dd>
        {Object.entries(deposit.notice.bankAccount).map(([key, value]) => <div className="col-12" key={key}>
          <dt>{t(`pingDeposit.bank.${key}`)}</dt><dd>{value}</dd>
        </div>)}
      </>}
    </dl>
    {canShowClassic && <div className="mb-3">
      <button type="button" data-testid="ping-print" className="btn btn-outline-secondary btn-lg" disabled={fontState !== 'ready' || !documentContext} onClick={() => window.print()}>{t('pingDeposit.classic.print')}</button>
      <p className="text-explain mt-2">{t('pingDeposit.classic.scanHelp')}</p>
      {fontState !== 'ready' && <p role="status">{t(fontState === 'error' ? 'pingDeposit.classic.loadError' : 'pingDeposit.classic.loading')}</p>}
      {!documentContext && <p role="status">{t(documentError ? 'pingDeposit.document.loadError' : 'pingDeposit.document.loading')}</p>}
      {createPortal(<div className="ping-classic-print d-none d-print-block">
        {fontState === 'ready' && documentContext ? <PingPaymentDocument deposit={deposit} context={documentContext} /> : <p>{t('pingDeposit.classic.loadError')}</p>}
      </div>, document.body)}
    </div>}
    {!canShowClassic && deposit.mode !== 'mock' && classicSlipAmount(deposit) !== null && deposit.notice?.bankAccount.bankgiro_number && <p className="text-explain">{t('pingDeposit.classic.unavailable')}</p>}
    {deposit.mode === 'mock' && <p className="alert alert-warning">{t('pingDeposit.mock')}</p>}
    <p className="text-explain">{t('pingDeposit.ownership')}</p>
    <p className="text-explain">{t('pingDeposit.payoutBlocked')}</p>
    {error && <p role="alert" className="alert alert-danger">{t('pingDeposit.refreshError')}</p>}
    <div className="d-grid gap-2 d-sm-flex flex-wrap">
      <button type="button" data-testid="ping-refresh" className="btn btn-primary btn-lg" disabled={busy} onClick={() => void refresh()}>{t(busy ? 'pingDeposit.checking' : 'pingDeposit.refresh')}</button>
      <button type="button" className="btn btn-outline-secondary btn-lg" onClick={onBack}>{t('paymentCheckout.backToProject')}</button>
    </div>
    {deposit.debugBankAvailable && deposit.mode === 'sandbox' && deposit.notice && <details className="mt-4 d-print-none">
      <summary className="btn btn-outline-secondary btn-lg">{t('debugBank.menu')}</summary>
      <p className="text-explain mt-3">{t('debugBank.description')}</p>
      <a className="btn btn-outline-primary btn-lg" href={`/debug-bank/${encodeURIComponent(deposit.paymentId)}`}>{t('debugBank.open')}</a>
    </details>}
    {canSimulate && deposit.mode === 'mock' && deposit.status !== 'funded' && <fieldset className="mt-4" disabled={busy}>
      <legend className="h6">{t('pingDeposit.simulationTitle')}</legend>
      <label htmlFor="ping-mock-amount" className="form-label">{t('pingDeposit.simulationAmount')}</label>
      <input id="ping-mock-amount" className="form-control form-control-lg" inputMode="decimal" disabled={simulation.current !== null} value={amount} onChange={event => setAmount(event.target.value)} />
      <button type="button" className="btn btn-outline-primary btn-lg mt-2 w-100" disabled={!amount} onClick={() => void refresh(true)}>{t('pingDeposit.simulate')}</button>
    </fieldset>}
  </section>;
}
